/**
 * PublishModerationTab — Super Admin moderation panel for the Publish
 * feature. Three sections stacked vertically:
 *
 *   1. Reports queue  — open user reports; one-click Dismiss / Hide /
 *                       Delete on each. Post preview inline.
 *   2. Flagged posts  — posts with report_count > 0 OR status != published
 *                       (hidden / deleted / draft); Hide / Unhide / Delete
 *                       affordances.
 *   3. Settings       — auto-hide + auto-delete thresholds, public landing
 *                       slug, moderation note, default topics editor.
 *
 * Backend endpoints live at /api/publish/moderation/*.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Flag, Prohibit, Trash, ArrowClockwise, Warning, Gear, Eye, EyeSlash,
  X, Plus,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import {
  modListReports, modListPosts, modHidePost, modUnhidePost, modDeletePost,
  modResolveReport, modGetSettings, modPutSettings,
} from "@/lib/publishApi";
import { formatApiError } from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 2592000) return `${Math.floor(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

const STATUS_STYLE = {
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
  hidden:    "bg-amber-100 text-amber-800 border-amber-200",
  deleted:   "bg-rose-100 text-rose-800 border-rose-200",
  draft:     "bg-slate-100 text-slate-700 border-slate-200",
};

const CATEGORY_LABEL = {
  spam: "Spam", harassment: "Harassment", misinformation: "Misinformation",
  inappropriate: "Inappropriate", copyright: "Copyright", other: "Other",
};

export default function PublishModerationTab() {
  const [reportsTab, setReportsTab] = useState("open"); // open|resolved|all
  const [reports, setReports] = useState([]);
  const [reportsBusy, setReportsBusy] = useState(false);
  const [posts, setPosts] = useState([]);
  const [postsFilter, setPostsFilter] = useState({ status: "", min_reports: 0 });
  const [postsBusy, setPostsBusy] = useState(false);
  const [pending, setPending] = useState(null); // per-row loading key

  // Settings
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [topicDraft, setTopicDraft] = useState("");

  const loadReports = useCallback(async () => {
    setReportsBusy(true);
    try {
      const { items } = await modListReports(reportsTab, 100, 0);
      setReports(items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReportsBusy(false); }
  }, [reportsTab]);

  const loadPosts = useCallback(async () => {
    setPostsBusy(true);
    try {
      const { items } = await modListPosts({
        status: postsFilter.status || undefined,
        min_reports: postsFilter.min_reports || undefined,
        limit: 50,
      });
      setPosts(items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPostsBusy(false); }
  }, [postsFilter]);

  const loadSettings = useCallback(async () => {
    try {
      const s = await modGetSettings();
      setSettings(s);
      setSettingsForm({
        public_landing_enabled: !!s.public_landing_enabled,
        public_landing_slug: s.public_landing_slug || "publish",
        auto_hide_threshold: Number(s.auto_hide_threshold || 2),
        auto_delete_threshold: Number(s.auto_delete_threshold || 5),
        moderation_note: s.moderation_note || "",
        default_topics: [...(s.default_topics || [])],
      });
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  // ---- Actions ----
  const doResolve = async (rid, action) => {
    setPending(`report-${rid}`);
    try {
      await modResolveReport(rid, action);
      toast.success(
        action === "dismiss" ? "Report dismissed" :
        action === "hide"    ? "Report resolved — post hidden" :
        "Report resolved — post deleted"
      );
      await Promise.all([loadReports(), loadPosts()]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPending(null); }
  };

  const doHide = async (pid) => {
    const note = window.prompt("Reason for hiding this post? (optional)") || "";
    setPending(`post-${pid}`);
    try {
      await modHidePost(pid, note);
      toast.success("Post hidden");
      loadPosts();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPending(null); }
  };

  const doUnhide = async (pid) => {
    setPending(`post-${pid}`);
    try {
      await modUnhidePost(pid, "");
      toast.success("Post restored");
      loadPosts();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPending(null); }
  };

  const doDelete = async (pid) => {
    if (!window.confirm("Delete this post? Author-facing status becomes 'deleted'. This is reversible via DB, not from the UI.")) return;
    setPending(`post-${pid}`);
    try {
      await modDeletePost(pid);
      toast.success("Post deleted");
      loadPosts();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPending(null); }
  };

  const saveSettings = async () => {
    if (!settingsForm) return;
    if (Number(settingsForm.auto_hide_threshold) >= Number(settingsForm.auto_delete_threshold)) {
      toast.error("Auto-hide threshold must be less than auto-delete threshold");
      return;
    }
    setSettingsBusy(true);
    try {
      const body = {
        public_landing_enabled: settingsForm.public_landing_enabled,
        public_landing_slug: (settingsForm.public_landing_slug || "").trim() || "publish",
        auto_hide_threshold: Number(settingsForm.auto_hide_threshold),
        auto_delete_threshold: Number(settingsForm.auto_delete_threshold),
        moderation_note: settingsForm.moderation_note || "",
        default_topics: settingsForm.default_topics,
      };
      const updated = await modPutSettings(body);
      setSettings(updated);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSettingsBusy(false); }
  };

  const addTopic = () => {
    const t = topicDraft.trim().replace(/^#/, "");
    if (!t) return;
    if ((settingsForm?.default_topics || []).includes(t)) { setTopicDraft(""); return; }
    setSettingsForm(f => ({ ...f, default_topics: [...(f.default_topics || []), t] }));
    setTopicDraft("");
  };

  const removeTopic = (t) => {
    setSettingsForm(f => ({ ...f, default_topics: (f.default_topics || []).filter(x => x !== t) }));
  };

  const openCount = useMemo(
    () => reports.filter(r => !r.resolution).length,
    [reports]
  );

  return (
    <div className="space-y-6" data-testid="publish-moderation-tab">
      {/* ================== Reports queue ================== */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Flag size={18} weight="duotone" className="text-rose-600" />
              Reports queue
              {openCount > 0 && reportsTab === "open" && (
                <Badge className="ml-1 bg-rose-600 hover:bg-rose-700">{openCount} open</Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              User-submitted reports on published or hidden posts.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={reportsTab} onValueChange={setReportsTab}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="reports-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" onClick={loadReports} disabled={reportsBusy}
                    data-testid="reports-refresh"
                    aria-label="Refresh reports">
              <ArrowClockwise size={14} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {reportsBusy ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
          ) : reports.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center"
                 data-testid="reports-empty">
              No reports {reportsTab === "open" ? "in the queue" : "found"}.
            </div>
          ) : (
            <div className="divide-y">
              {reports.map(r => (
                <ReportRow
                  key={r.id} report={r}
                  pending={pending === `report-${r.id}`}
                  onResolve={doResolve}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ================== Flagged posts ================== */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Warning size={18} weight="duotone" className="text-amber-600" />
              Flagged & moderated posts
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Posts with reports or non-published status.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={postsFilter.status || "any"}
                    onValueChange={(v) => setPostsFilter(f => ({ ...f, status: v === "any" ? "" : v }))}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="posts-status-filter">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any status</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
                <SelectItem value="deleted">Deleted</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number" min={0} className="w-24 h-8 text-xs"
              value={postsFilter.min_reports}
              onChange={e => setPostsFilter(f => ({ ...f, min_reports: Number(e.target.value) || 0 }))}
              placeholder="Min reports"
              data-testid="posts-min-reports"
            />
            <Button size="sm" variant="ghost" onClick={loadPosts} disabled={postsBusy}
                    aria-label="Refresh posts">
              <ArrowClockwise size={14} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {postsBusy ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center"
                 data-testid="posts-empty">
              No posts match the filter.
            </div>
          ) : (
            <div className="divide-y">
              {posts.map(p => (
                <PostRow
                  key={p.id} post={p}
                  pending={pending === `post-${p.id}`}
                  onHide={doHide} onUnhide={doUnhide} onDelete={doDelete}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ================== Settings ================== */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gear size={18} weight="duotone" />
            Publish settings
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Auto-moderation thresholds, public landing config, and default topic suggestions.
          </p>
        </CardHeader>
        <CardContent>
          {!settingsForm ? (
            <div className="text-sm text-muted-foreground py-4">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Auto-hide threshold
                </Label>
                <Input type="number" min={1} value={settingsForm.auto_hide_threshold}
                       onChange={e => setSettingsForm(f => ({ ...f, auto_hide_threshold: e.target.value }))}
                       data-testid="setting-auto-hide" />
                <p className="text-[11px] text-muted-foreground">
                  Post is auto-hidden when unresolved reports reach this count.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Auto-delete threshold
                </Label>
                <Input type="number" min={2} value={settingsForm.auto_delete_threshold}
                       onChange={e => setSettingsForm(f => ({ ...f, auto_delete_threshold: e.target.value }))}
                       data-testid="setting-auto-delete" />
                <p className="text-[11px] text-muted-foreground">
                  Post is auto-deleted at this count. Must exceed the hide threshold.
                </p>
              </div>
              <div className="space-y-1.5 md:col-span-2 flex flex-wrap items-center gap-3 border border-border rounded-md p-3 bg-muted/40">
                <div className="flex items-center gap-2">
                  <Switch checked={settingsForm.public_landing_enabled}
                          onCheckedChange={v => setSettingsForm(f => ({ ...f, public_landing_enabled: v }))}
                          data-testid="setting-public-toggle" />
                  <Label className="text-sm font-semibold m-0">Public landing page enabled</Label>
                </div>
                <div className="flex items-center gap-1 ml-auto text-xs text-muted-foreground">
                  <span>URL slug:</span>
                  <span className="font-mono">/</span>
                  <Input value={settingsForm.public_landing_slug}
                         onChange={e => setSettingsForm(f => ({ ...f, public_landing_slug: e.target.value }))}
                         className="h-7 w-32 text-xs font-mono"
                         data-testid="setting-landing-slug" />
                  <Link to="/publish" target="_blank"
                        className="text-sky-700 hover:text-sky-900 text-xs underline ml-1"
                        data-testid="setting-preview-link">
                    Preview →
                  </Link>
                </div>
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Default topic suggestions
                </Label>
                <div className="flex flex-wrap items-center gap-1.5">
                  {(settingsForm.default_topics || []).map(t => (
                    <Badge key={t} variant="outline" className="gap-1 pl-2 pr-1">
                      #{t}
                      <button type="button" onClick={() => removeTopic(t)}
                              className="hover:bg-muted rounded px-0.5"
                              data-testid={`topic-remove-${t}`}>
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}
                  <Input value={topicDraft} onChange={e => setTopicDraft(e.target.value)}
                         onKeyDown={(e) => {
                           if (e.key === "Enter" || e.key === ",") {
                             e.preventDefault(); addTopic();
                           }
                         }}
                         placeholder="Add topic and press Enter"
                         className="flex-1 min-w-[180px] h-8 text-xs"
                         data-testid="setting-topic-input" />
                  <Button size="icon" variant="outline" onClick={addTopic}
                          data-testid="setting-topic-add"><Plus size={14} /></Button>
                </div>
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Moderation note (internal)
                </Label>
                <Textarea rows={2} value={settingsForm.moderation_note || ""}
                          onChange={e => setSettingsForm(f => ({ ...f, moderation_note: e.target.value }))}
                          placeholder="Optional note visible only to super admins reviewing this queue."
                          data-testid="setting-mod-note" />
              </div>

              <div className="md:col-span-2 flex items-center justify-end gap-2 pt-2">
                <span className="text-[11px] text-muted-foreground mr-auto">
                  Last updated {settings?.updated_at ? timeAgo(settings.updated_at) : "—"}
                </span>
                <Button variant="outline" onClick={loadSettings}
                        disabled={settingsBusy}>Reset</Button>
                <Button onClick={saveSettings} disabled={settingsBusy}
                        data-testid="setting-save-btn">
                  {settingsBusy ? "Saving…" : "Save settings"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// -------------------- Row components --------------------

function ReportRow({ report, pending, onResolve }) {
  const p = report.post || {};
  const resolved = !!report.resolution;
  return (
    <div className="py-3 flex gap-4 items-start" data-testid={`report-row-${report.id}`}>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
          <Badge className="bg-rose-100 text-rose-800 border-rose-200" variant="outline">
            {CATEGORY_LABEL[report.category] || "Other"}
          </Badge>
          <span className="font-semibold truncate">
            {p.title || (p.post_type ? `${p.post_type} post` : "Post")}
          </span>
          {p.status && (
            <Badge variant="outline" className={STATUS_STYLE[p.status] || ""}>
              {p.status}
            </Badge>
          )}
          {typeof p.report_count === "number" && p.report_count > 0 && (
            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
              {p.report_count} reports
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground break-words">{report.reason}</p>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          <span>Reporter: <b>{report.reporter_name || "?"}</b></span>
          <span>· {timeAgo(report.created_at)}</span>
          {p.workspace_name && <span>· Workspace: {p.workspace_name}</span>}
          {p.author_name && <span>· Author: {p.author_name}</span>}
          {p.id && (
            <Link to={`/app/publish/${p.id}`} target="_blank"
                  className="text-sky-700 hover:text-sky-900 underline"
                  data-testid={`report-open-post-${report.id}`}>
              Open post →
            </Link>
          )}
        </div>
        {resolved && (
          <div className="text-[11px] text-emerald-700">
            Resolved: {report.resolution}
          </div>
        )}
      </div>
      {!resolved && (
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button size="sm" variant="outline" disabled={pending}
                  onClick={() => onResolve(report.id, "dismiss")}
                  data-testid={`report-dismiss-${report.id}`}>
            Dismiss
          </Button>
          <Button size="sm" variant="outline" disabled={pending}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  onClick={() => onResolve(report.id, "hide")}
                  data-testid={`report-hide-${report.id}`}>
            <EyeSlash size={12} className="mr-1" /> Hide
          </Button>
          <Button size="sm" variant="outline" disabled={pending}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => onResolve(report.id, "delete")}
                  data-testid={`report-delete-${report.id}`}>
            <Trash size={12} className="mr-1" /> Delete
          </Button>
        </div>
      )}
    </div>
  );
}

function PostRow({ post, pending, onHide, onUnhide, onDelete }) {
  return (
    <div className="py-3 flex gap-4 items-start" data-testid={`mod-post-row-${post.id}`}>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
          <span className="font-semibold truncate">
            {post.title || `${(post.post_type || "post").toUpperCase()} · ${post.id.slice(0, 8)}`}
          </span>
          {post.status && (
            <Badge variant="outline" className={STATUS_STYLE[post.status] || ""}>
              {post.status}
            </Badge>
          )}
          {post.hidden_reason && (
            <Badge variant="outline" className="bg-muted">
              by {post.hidden_reason.replace(/_/g, " ")}
            </Badge>
          )}
          {(post.report_count || 0) > 0 && (
            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
              {post.report_count} reports
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          {post.author_name && <span>Author: <b>{post.author_name}</b></span>}
          {post.workspace_name && <span>· {post.workspace_name}</span>}
          <span>· Updated {timeAgo(post.updated_at)}</span>
          <Link to={`/app/publish/${post.id}`} target="_blank"
                className="text-sky-700 hover:text-sky-900 underline"
                data-testid={`mod-open-post-${post.id}`}>
            Open post →
          </Link>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        {post.status === "hidden" ? (
          <Button size="sm" variant="outline" disabled={pending}
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                  onClick={() => onUnhide(post.id)}
                  data-testid={`mod-unhide-${post.id}`}>
            <Eye size={12} className="mr-1" /> Unhide
          </Button>
        ) : post.status === "published" ? (
          <Button size="sm" variant="outline" disabled={pending}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  onClick={() => onHide(post.id)}
                  data-testid={`mod-hide-${post.id}`}>
            <EyeSlash size={12} className="mr-1" /> Hide
          </Button>
        ) : null}
        {post.status !== "deleted" && (
          <Button size="sm" variant="outline" disabled={pending}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => onDelete(post.id)}
                  data-testid={`mod-delete-${post.id}`}>
            <Trash size={12} className="mr-1" /> Delete
          </Button>
        )}
      </div>
    </div>
  );
}
